import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import TaskPhasePrompt from './TaskPhasePrompt';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ESTADO_BADGES = {
  pendiente_agendar: { label: 'Pendiente de agendar', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  programada: { label: 'Programada', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  vencida: { label: 'Vencida', cls: 'bg-red-100 text-red-700 border-red-200' },
  completada: { label: 'Completada', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  cancelada: { label: 'Cancelada', cls: 'bg-slate-200 text-slate-500 border-slate-300' },
};

const fmtFecha = (t) => {
  if (!t.fecha) return '';
  return t.hora ? `${t.fecha} ${t.hora}` : t.fecha;
};

const EstadoBadge = ({ estado }) => {
  const b = ESTADO_BADGES[estado] || ESTADO_BADGES.pendiente_agendar;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span>;
};

const CreateTaskForm = ({ assetId, assignableUsers, currentUser, onCreated, onCancel }) => {
  const [tarea, setTarea] = useState('');
  const [responsableId, setResponsableId] = useState(currentUser?.id || '');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!tarea.trim() || !responsableId) { toast.error('Tarea y responsable son obligatorios'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/assets/${assetId}/tasks`, {
        tarea: tarea.trim(), responsable_id: responsableId,
        fecha: fecha || null, hora: hora || null, notas_iniciales: notas.trim() || null,
      }, { withCredentials: true });
      toast.success('Tarea creada');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear la tarea');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 rounded-lg border space-y-3" style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }} data-testid="task-create-form">
      <div className="grid md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-sm text-slate-600 font-medium">Tarea *</label>
          <input type="text" className="input-base" value={tarea} onChange={(e) => setTarea(e.target.value)}
            placeholder="Ej. Llamar al notario para firma" data-testid="task-input-tarea" />
        </div>
        <div>
          <label className="text-sm text-slate-600 font-medium">Responsable *</label>
          <select className="input-base" value={responsableId} onChange={(e) => setResponsableId(e.target.value)} data-testid="task-select-responsable">
            <option value="">Selecciona un responsable</option>
            {(assignableUsers || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-slate-600">Fecha</label>
            <input type="date" className="input-base" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="task-input-fecha" />
          </div>
          <div>
            <label className="text-sm text-slate-600">Hora</label>
            <input type="time" className="input-base" value={hora} onChange={(e) => setHora(e.target.value)} disabled={!fecha} data-testid="task-input-hora" />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-sm text-slate-600">Notas iniciales</label>
          <textarea className="input-base min-h-[50px]" value={notas} onChange={(e) => setNotas(e.target.value)} data-testid="task-input-notas" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-2 text-sm rounded border" style={{ borderColor: 'var(--color-border)' }} data-testid="task-create-cancel-btn">Cancelar</button>
        <button onClick={handleSubmit} disabled={saving} className="btn-primary" data-testid="task-create-submit-btn">
          {saving ? 'Guardando…' : '+ Crear tarea'}
        </button>
      </div>
    </div>
  );
};

const RescheduleForm = ({ task, assignableUsers, onSave, onCancel }) => {
  const [tarea, setTarea] = useState(task.tarea);
  const [responsableId, setResponsableId] = useState(task.responsable_id || '');
  const [fecha, setFecha] = useState(task.fecha || '');
  const [hora, setHora] = useState(task.hora || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave({ tarea, responsable_id: responsableId, fecha: fecha || null, hora: hora || null, clear_fecha: !fecha }); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border space-y-2" style={{ background: 'var(--color-bg-page)', borderColor: 'var(--color-border)' }} data-testid="task-reschedule-form">
      <input type="text" className="input-base" value={tarea} onChange={(e) => setTarea(e.target.value)} data-testid="task-reschedule-input-tarea" />
      <div className="grid grid-cols-3 gap-2">
        <select className="input-base" value={responsableId} onChange={(e) => setResponsableId(e.target.value)} data-testid="task-reschedule-select-responsable">
          {(assignableUsers || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        <input type="date" className="input-base" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="task-reschedule-input-fecha" />
        <input type="time" className="input-base" value={hora} onChange={(e) => setHora(e.target.value)} disabled={!fecha} data-testid="task-reschedule-input-hora" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border" style={{ borderColor: 'var(--color-border)' }}>Cancelar</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary px-3 py-1.5 text-sm" data-testid="task-reschedule-save-btn">
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
};

const CloseTaskForm = ({ mode, onSave, onCancel }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const label = mode === 'complete' ? 'Nota de resolución *' : 'Motivo de cancelación *';
  const handleSave = async () => {
    if (!text.trim()) { toast.error('Este campo es obligatorio'); return; }
    setSaving(true);
    try { await onSave(text.trim()); } finally { setSaving(false); }
  };
  return (
    <div className="mt-3 p-3 rounded-lg border space-y-2" style={{ background: 'var(--color-bg-page)', borderColor: 'var(--color-border)' }} data-testid={`task-${mode}-form`}>
      <label className="text-sm text-slate-600 font-medium">{label}</label>
      <textarea className="input-base min-h-[60px]" value={text} onChange={(e) => setText(e.target.value)} data-testid={`task-${mode}-input`} />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border" style={{ borderColor: 'var(--color-border)' }}>Volver</button>
        <button onClick={handleSave} disabled={saving}
          className={`px-3 py-1.5 text-sm font-semibold rounded text-white ${mode === 'complete' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
          data-testid={`task-${mode}-confirm-btn`}>
          {saving ? 'Guardando…' : mode === 'complete' ? 'Completar tarea' : 'Cancelar tarea'}
        </button>
      </div>
    </div>
  );
};

const TaskCard = ({ task, assignableUsers, onRefresh, onLastTaskClosed, readOnly }) => {
  const [actionMode, setActionMode] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const isOpen = task.estado === 'pendiente_agendar' || task.estado === 'programada';

  const handleReschedule = async (payload) => {
    try {
      await axios.patch(`${API}/assets/${task.asset_id}/tasks/${task.id}`, payload, { withCredentials: true });
      toast.success('Tarea reprogramada');
      setActionMode(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reprogramar');
    }
  };

  const handleComplete = async (notaResolucion) => {
    try {
      const r = await axios.post(`${API}/assets/${task.asset_id}/tasks/${task.id}/complete`,
        { nota_resolucion: notaResolucion }, { withCredentials: true });
      toast.success('Tarea completada');
      setActionMode(null);
      onRefresh();
      if (r.data?.remaining_open_tasks === 0 && r.data?.asset_etapa_visual === 'en_gestion') {
        onLastTaskClosed();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al completar');
    }
  };

  const handleCancel = async (motivo) => {
    try {
      await axios.post(`${API}/assets/${task.asset_id}/tasks/${task.id}/cancel`, { motivo }, { withCredentials: true });
      toast.success('Tarea cancelada');
      setActionMode(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cancelar');
    }
  };

  return (
    <div className="p-4 rounded-lg border" style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }} data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <EstadoBadge estado={task.estado_display} />
            <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{fmtFecha(task)}</span>
          </div>
          <p className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>{task.tarea}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>Responsable: {task.responsable_nombre || '—'}</p>
          {task.notas_iniciales && <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{task.notas_iniciales}</p>}
          {task.estado === 'completada' && task.nota_resolucion && (
            <p className="text-xs mt-1 text-emerald-700"><strong>Resuelta:</strong> {task.nota_resolucion}</p>
          )}
          {task.estado === 'cancelada' && task.motivo_cancelacion && (
            <p className="text-xs mt-1 text-red-600"><strong>Motivo:</strong> {task.motivo_cancelacion}</p>
          )}
        </div>
        {isOpen && !actionMode && !readOnly && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setActionMode('reschedule')} className="text-xs font-medium px-2 py-1 rounded border" style={{ borderColor: 'var(--color-border)' }} data-testid={`task-reschedule-btn-${task.id}`}>Reprogramar</button>
            <button onClick={() => setActionMode('complete')} className="text-xs font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`task-complete-btn-${task.id}`}>Completar</button>
            <button onClick={() => setActionMode('cancel')} className="text-xs font-medium px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100" data-testid={`task-cancel-btn-${task.id}`}>Cancelar</button>
          </div>
        )}
      </div>

      {actionMode === 'reschedule' && (
        <RescheduleForm task={task} assignableUsers={assignableUsers} onSave={handleReschedule} onCancel={() => setActionMode(null)} />
      )}
      {actionMode === 'complete' && (
        <CloseTaskForm mode="complete" onSave={handleComplete} onCancel={() => setActionMode(null)} />
      )}
      {actionMode === 'cancel' && (
        <CloseTaskForm mode="cancel" onSave={handleCancel} onCancel={() => setActionMode(null)} />
      )}

      {Array.isArray(task.history) && task.history.length > 0 && (
        <div className="mt-2">
          <button onClick={() => setShowHistory((s) => !s)} className="text-xs underline" style={{ color: 'var(--color-text-muted)' }} data-testid={`task-history-toggle-${task.id}`}>
            {showHistory ? 'Ocultar historial' : `Ver historial (${task.history.length})`}
          </button>
          {showHistory && (
            <ul className="mt-1 space-y-1">
              {task.history.map((h, idx) => (
                <li key={idx} className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {new Date(h.at).toLocaleString('es-ES')} · {h.by} · {h.action} ({(h.changed || []).join(', ')})
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const TasksSection = ({ assetId, tasks, assignableUsers, currentUser, assetEtapaVisual, readOnly, onRefresh, onAssetRefresh }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const list = tasks || [];
  const open = list.filter((t) => t.estado === 'pendiente_agendar' || t.estado === 'programada');
  const closed = list.filter((t) => t.estado === 'completada' || t.estado === 'cancelada');

  const rank = { vencida: 0, programada: 1, pendiente_agendar: 2 };
  const openSorted = [...open].sort((a, b) => {
    const r = (rank[a.estado_display] ?? 9) - (rank[b.estado_display] ?? 9);
    if (r !== 0) return r;
    return (a.fecha || '').localeCompare(b.fecha || '');
  });

  return (
    <div className="card-base p-6" data-testid="tasks-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="heading-3" data-testid="tasks-pending-header">Tareas pendientes · {open.length}</h3>
        {!showCreateForm && !readOnly && (
          <button onClick={() => setShowCreateForm(true)} className="btn-primary" data-testid="task-open-create-form-btn">+ Nueva tarea</button>
        )}
      </div>

      {showCreateForm && !readOnly && (
        <div className="mb-5">
          <CreateTaskForm
            assetId={assetId}
            assignableUsers={assignableUsers}
            currentUser={currentUser}
            onCreated={() => { setShowCreateForm(false); onRefresh(); }}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {openSorted.length === 0 ? (
        <p className="text-sm text-center py-4" style={{ color: 'var(--color-text-muted)' }}>No hay tareas pendientes para este activo.</p>
      ) : (
        <div className="space-y-3" data-testid="tasks-open-list">
          {openSorted.map((t) => (
            <TaskCard key={t.id} task={t} assignableUsers={assignableUsers} onRefresh={onRefresh} onLastTaskClosed={() => setShowPrompt(true)} readOnly={readOnly} />
          ))}
        </div>
      )}

      {closed.length > 0 && (
        <div className="mt-6">
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Historial · {closed.length}</p>
          <div className="space-y-3" data-testid="tasks-closed-list">
            {closed.map((t) => (
              <TaskCard key={t.id} task={t} assignableUsers={assignableUsers} onRefresh={onRefresh} onLastTaskClosed={() => {}} readOnly={readOnly} />
            ))}
          </div>
        </div>
      )}

      <TaskPhasePrompt
        open={showPrompt}
        assetId={assetId}
        currentEtapaVisual={assetEtapaVisual}
        onClose={() => setShowPrompt(false)}
        onCreateTask={() => { setShowPrompt(false); setShowCreateForm(true); }}
        onDone={() => { setShowPrompt(false); onAssetRefresh(); onRefresh(); }}
      />
    </div>
  );
};

export default TasksSection;
